// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { convertHeicToJpeg, compressImage } from '@/utils/image';

export interface UserCollection {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  visibility: 'public' | 'friends' | 'private';
  cover_image: string | null;
  created_at: string;
}

export interface CollectionItem {
  id: string;
  collection_id: string;
  artwork_id?: string | null;
  media_type: 'image' | 'video';
  media_url: string;
  title?: string | null;
  notes?: string | null;
  position: number;
  created_at: string;
}

export const listMyCollections = async (): Promise<UserCollection[]> => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) return [] as any[];
  const { data, error } = await supabase
    .from('user_collections')
    .select('*')
    .eq('user_id', me)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const createCollection = async (payload: { title: string; description?: string; visibility?: 'public' | 'friends' | 'private' }): Promise<string> => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('user_collections')
    .insert({
      user_id: me,
      title: payload.title,
      description: payload.description || null,
      visibility: payload.visibility || 'public',
    })
    .select('id')
    .single();
  if (error) throw error;
  return data.id as string;
};

export const updateCollection = async (id: string, patch: Partial<UserCollection>) => {
  const { error } = await supabase
    .from('user_collections')
    .update(patch as any)
    .eq('id', id);
  if (error) throw error;
};

export const deleteCollection = async (id: string) => {
  const { error } = await supabase
    .from('user_collections')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

export const listCollectionItems = async (collectionId: string): Promise<CollectionItem[]> => {
  const { data, error } = await supabase
    .from('user_collection_items')
    .select('*')
    .eq('collection_id', collectionId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const addItemFile = async (collectionId: string, file: File, title?: string): Promise<void> => {
  // Determine media type constraints
  const isImage = file.type.startsWith('image/') || /\.heic$/i.test(file.name);
  const isVideo = file.type.startsWith('video/');
  if (!isImage && !isVideo) {
    throw new Error('Only image or video files are allowed');
  }
  if (isVideo && file.type !== 'video/mp4') {
    throw new Error('Only mp4 videos are allowed');
  }
  if (isVideo && file.size > 200 * 1024 * 1024) {
    throw new Error('Video size exceeds 200MB');
  }

  let toUpload = file;
  if (isImage) {
    try {
      if (file.type === 'image/heic' || /\.heic$/i.test(file.name)) {
        toUpload = await convertHeicToJpeg(file);
      }
      // Compress images if large (kept under 100MB)
      toUpload = await compressImage(toUpload, 100);
    } catch {
      // keep original on failure
    }
  }

  // Create item ID first for path layout
  const itemId = uuidv4();
  const path = `collection/${collectionId}/${itemId}/${Date.now()}-${toUpload.name}`;

  // Upload to storage
  const bucket = 'collection-media';
  const { error: upErr } = await supabase.storage.from(bucket).upload(path, toUpload, { upsert: false, contentType: toUpload.type });
  if (upErr) throw upErr;

  // Insert DB row with provided id and storage path
  const mediaType = isImage ? 'image' : 'video';
  const { error: insErr } = await supabase
    .from('user_collection_items')
    .insert({ id: itemId, collection_id: collectionId, media_type: mediaType, media_url: path, title: title || null });
  if (insErr) throw insErr;
};

export const removeItem = async (itemId: string) => {
  const { error } = await supabase
    .from('user_collection_items')
    .delete()
    .eq('id', itemId);
  if (error) throw error;
};


